import { useEffect, useMemo, useState } from 'react';
import type { ScheduleType } from '../settings/settingsApi';
import type { Task } from '../tasks/tasksApi';

type CalendarMode = 'dates_and_points' | 'points_only';

interface CalendarViewProps {
  tasks: Task[];
  pointsPerDay: number;
  scheduleType: ScheduleType;
  onSaveVelocity?: (value: number) => Promise<void>;
  onSaveScheduleType?: (type: ScheduleType) => Promise<void>;
  onTaskClick?: (task: Task) => void;
}

interface ProjectedTask {
  task: Task;
  start: Date;
  end: Date;
  /** true = sequentially projected, false = locked to explicit dates */
  isProjected: boolean;
  color: string;
}

// ─── Date helpers ────────────────────────────────────────────────────────────

const PALETTE = ['#3b82f6', '#8b5cf6', '#f59e0b', '#10b981', '#ef4444', '#ec4899', '#0891b2', '#65a30d'];

function hashColor(id: string): string {
  let h = 0;
  for (const c of id) h = (h * 31 + c.charCodeAt(0)) & 0xffff;
  return PALETTE[h % PALETTE.length];
}

function stripTime(d: Date): Date {
  const c = new Date(d);
  c.setHours(0, 0, 0, 0);
  return c;
}

function isWeekend(d: Date): boolean {
  return d.getDay() === 0 || d.getDay() === 6;
}

// Parse an ISO date string "YYYY-MM-DD" as LOCAL midnight (not UTC).
// new Date("2026-06-11") would give UTC midnight → wrong day in UTC− zones.
function parseDate(s: string): Date {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function toDateInput(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Last day of a task that starts on `from` and occupies `days` business-days of work.
 * The start day counts as day 1, so a 1-pt task starts and ends on `from`.
 */
function endBiz(from: Date, days: number): Date {
  const d = new Date(from);
  let rem = Math.max(0, Math.ceil(days) - 1); // additional days after start
  while (rem > 0) {
    d.setDate(d.getDate() + 1);
    if (!isWeekend(d)) rem--;
  }
  return d;
}

/**
 * Last day of a task that starts on `from` and occupies `days` calendar-days.
 * The start day counts as day 1.
 */
function endCal(from: Date, days: number): Date {
  const d = new Date(from);
  d.setDate(d.getDate() + Math.max(0, Math.ceil(days) - 1));
  return d;
}

function nextBizDay(from: Date): Date {
  const d = new Date(from);
  do { d.setDate(d.getDate() + 1); } while (isWeekend(d));
  return d;
}

function nextDay(from: Date): Date {
  const d = new Date(from);
  d.setDate(d.getDate() + 1);
  return d;
}

function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth()
    && a.getDate() === b.getDate();
}

function fmtDate(d: Date): string {
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

// ─── Projection ──────────────────────────────────────────────────────────────

/**
 * Two modes:
 *
 * dates_and_points — Tasks that have BOTH startDate AND endDate set, and whose
 *   endDate is on or after projStart, are rendered at their locked dates.
 *   Past-dated incomplete tasks and all tasks without explicit dates are placed
 *   sequentially starting from projStart using velocity.
 *
 * points_only — All incomplete tasks are placed sequentially regardless of
 *   any dates on the task.
 *
 * Completed tasks always render at their recorded dates (if present).
 * Anchored tasks (dates_and_points mode) never affect the sequential cursor.
 */
function computeProjections(
  tasks: Task[],
  velocity: number,
  projStart: Date,
  scheduleType: ScheduleType,
  mode: CalendarMode,
): ProjectedTask[] {
  const v = Math.max(0.1, velocity);
  const biz = scheduleType !== 'ALL_DAYS';
  const result: ProjectedTask[] = [];

  const anchor = stripTime(projStart); // cutoff for "is this task still in the future?"

  // Sequential cursor — first valid work day on or after projStart
  let cursor = new Date(anchor);
  if (biz) while (isWeekend(cursor)) cursor.setDate(cursor.getDate() + 1);

  for (const task of tasks) {
    const color = task.completedAt ? '#22c55e' : hashColor(task.taskId);

    // ── Completed tasks ────────────────────────────────────────────────────
    // Show at recorded dates for historical reference; never touch cursor.
    if (task.completedAt) {
      if (task.startDate && task.endDate) {
        result.push({ task, start: parseDate(task.startDate), end: parseDate(task.endDate), isProjected: false, color });
      }
      continue;
    }

    // ── Anchored tasks (dates + points mode only) ──────────────────────────
    // A task is anchored if it has explicit start+end dates and its end hasn't
    // passed the projection start.  Anchored tasks are shown at their locked dates
    // and do NOT move the sequential cursor — the two tracks are independent.
    if (mode === 'dates_and_points' && task.startDate && task.endDate) {
      const s = parseDate(task.startDate);
      const e = parseDate(task.endDate);
      if (e >= anchor) {
        result.push({ task, start: s, end: e, isProjected: false, color });
        continue; // no cursor change
      }
      // Past-dated and not complete — treat as unscheduled, fall through to sequential
    }

    // ── Sequential projection ──────────────────────────────────────────────
    if (task.points <= 0) {
      // 0-point tasks: 1-day marker at current cursor, no time consumed
      result.push({ task, start: new Date(cursor), end: new Date(cursor), isProjected: true, color });
      continue;
    }

    const days = Math.ceil(task.points / v);
    const start = new Date(cursor);
    const end = biz ? endBiz(cursor, days) : endCal(cursor, days);
    result.push({ task, start, end, isProjected: true, color });
    cursor = biz ? nextBizDay(end) : nextDay(end);
  }

  return result;
}

// ─── Calendar grid ───────────────────────────────────────────────────────────

function calendarGrid(year: number, month: number): Date[][] {
  const first = new Date(year, month, 1);
  const last = new Date(year, month + 1, 0);

  const start = new Date(first);
  start.setDate(start.getDate() - start.getDay()); // back to Sunday

  const end = new Date(last);
  end.setDate(end.getDate() + (6 - end.getDay())); // forward to Saturday

  const weeks: Date[][] = [];
  const cur = new Date(start);
  while (cur <= end) {
    const week: Date[] = [];
    for (let i = 0; i < 7; i++) { week.push(new Date(cur)); cur.setDate(cur.getDate() + 1); }
    weeks.push(week);
  }
  return weeks;
}

const DAY_HDR = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const GRID = 'repeat(7, minmax(0, 1fr))';

// ─── Component ───────────────────────────────────────────────────────────────

export default function CalendarView({ tasks, pointsPerDay, scheduleType, onSaveVelocity, onSaveScheduleType, onTaskClick }: CalendarViewProps) {
  const today = useMemo(() => stripTime(new Date()), []);

  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [projStart, setProjStart] = useState(() => toDateInput(today));
  const [mode, setMode] = useState<CalendarMode>('dates_and_points');
  const [localVelocity, setLocalVelocity] = useState(pointsPerDay);
  const [savingVel, setSavingVel] = useState(false);
  const [savingMode, setSavingMode] = useState(false);

  useEffect(() => { setLocalVelocity(pointsPerDay); }, [pointsPerDay]);

  const isBiz = scheduleType !== 'ALL_DAYS';
  const velChanged = localVelocity !== pointsPerDay;

  async function saveVelocity() {
    if (!onSaveVelocity || !velChanged) return;
    setSavingVel(true);
    try { await onSaveVelocity(localVelocity); } finally { setSavingVel(false); }
  }

  async function toggleScheduleType() {
    if (!onSaveScheduleType) return;
    setSavingMode(true);
    try { await onSaveScheduleType(isBiz ? 'ALL_DAYS' : 'BUSINESS_DAYS'); }
    finally { setSavingMode(false); }
  }

  const projStartDate = useMemo(() => parseDate(projStart), [projStart]);

  const projected = useMemo(
    () => computeProjections(tasks, localVelocity, projStartDate, scheduleType, mode),
    [tasks, localVelocity, projStartDate, scheduleType, mode],
  );

  const remainingPts = useMemo(
    () => tasks.filter(t => !t.completedAt && t.points > 0).reduce((s, t) => s + t.points, 0),
    [tasks],
  );

  const allDone = useMemo(() => tasks.length > 0 && tasks.every(t => !!t.completedAt), [tasks]);

  const projEnd = useMemo(() => {
    const seq = projected.filter(p => p.isProjected && !p.task.completedAt);
    return seq.length ? seq.reduce((m, p) => (p.end > m ? p.end : m), seq[0].end) : null;
  }, [projected]);

  const estDays = Math.ceil(remainingPts / Math.max(0.1, localVelocity));
  const dayLabel = isBiz ? 'biz days' : 'days';

  const weeks = useMemo(() => calendarGrid(viewYear, viewMonth), [viewYear, viewMonth]);

  function prevMonth() {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  }
  function nextMonth() {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  }

  function tasksOnDay(day: Date): ProjectedTask[] {
    const d = stripTime(day);
    return projected.filter(p => {
      const s = stripTime(p.start);
      const e = stripTime(p.end);
      return d >= s && d <= e;
    });
  }

  return (
    <div>
      {/* ── Controls bar ── */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, alignItems: 'center', padding: '11px 14px', background: 'var(--color-card, #f9fafb)', border: '1px solid var(--color-border)', borderRadius: 8, marginBottom: 14 }}>

        {/* Projection mode */}
        <div style={{ display: 'flex', border: '1px solid var(--color-border)', borderRadius: 6, overflow: 'hidden' }}>
          {(['dates_and_points', 'points_only'] as CalendarMode[]).map(m => (
            <button
              key={m}
              type="button"
              onClick={() => setMode(m)}
              style={{
                padding: '3px 10px', fontSize: 12, border: 'none', cursor: 'pointer',
                background: mode === m ? 'var(--color-primary)' : 'transparent',
                color: mode === m ? '#fff' : 'var(--color-text-muted)',
              }}
            >
              {m === 'dates_and_points' ? 'Dates + Points' : 'Points Only'}
            </button>
          ))}
        </div>

        {/* Project-from date */}
        <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13 }}>
          <span style={{ color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>From</span>
          <input type="date" value={projStart} onChange={e => setProjStart(e.target.value)} style={{ fontSize: 13 }} />
        </label>

        {/* Velocity */}
        <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13 }}>
          <span style={{ color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>Velocity</span>
          <input
            type="number" min={1} step={1} value={localVelocity}
            onChange={e => setLocalVelocity(Math.max(1, Number(e.target.value)))}
            style={{ fontSize: 13, width: 60 }}
          />
          <span style={{ color: 'var(--color-text-muted)' }}>pts/day</span>
          {onSaveVelocity && velChanged && (
            <button type="button" onClick={saveVelocity} disabled={savingVel} style={{ fontSize: 12, padding: '2px 8px' }}>
              {savingVel ? 'Saving…' : 'Save'}
            </button>
          )}
        </label>

        {/* Business / All days toggle */}
        {onSaveScheduleType && (
          <div style={{ display: 'flex', border: '1px solid var(--color-border)', borderRadius: 6, overflow: 'hidden' }}>
            {(['BUSINESS_DAYS', 'ALL_DAYS'] as const).map(st => {
              const active = st === scheduleType;
              return (
                <button
                  key={st}
                  type="button"
                  onClick={active ? undefined : toggleScheduleType}
                  disabled={savingMode}
                  style={{
                    padding: '3px 10px', fontSize: 12, border: 'none',
                    cursor: active ? 'default' : 'pointer',
                    background: active ? 'var(--color-primary)' : 'transparent',
                    color: active ? '#fff' : 'var(--color-text-muted)',
                  }}
                >
                  {st === 'BUSINESS_DAYS' ? 'Business days' : 'All days'}
                </button>
              );
            })}
          </div>
        )}

        {/* Summary */}
        {remainingPts > 0 && projEnd ? (
          <span style={{ fontSize: 13, fontWeight: 600, marginLeft: 'auto' }}>
            {remainingPts} pts → <span style={{ color: 'var(--color-primary)' }}>{fmtDate(projEnd)}</span>
            <span style={{ fontWeight: 400, color: 'var(--color-text-muted)', marginLeft: 5 }}>(~{estDays} {dayLabel})</span>
          </span>
        ) : allDone ? (
          <span style={{ fontSize: 13, fontWeight: 600, color: '#22c55e', marginLeft: 'auto' }}>All complete ✓</span>
        ) : (
          <span style={{ fontSize: 13, color: 'var(--color-text-muted)', marginLeft: 'auto' }}>{remainingPts} pts remaining</span>
        )}
      </div>

      {/* ── Month nav ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <button type="button" onClick={prevMonth} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, padding: '0 4px', lineHeight: 1 }}>‹</button>
        <span style={{ fontWeight: 600, fontSize: 15, minWidth: 160, textAlign: 'center' }}>{MONTHS[viewMonth]} {viewYear}</span>
        <button type="button" onClick={nextMonth} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, padding: '0 4px', lineHeight: 1 }}>›</button>
        <button type="button" onClick={() => { setViewYear(today.getFullYear()); setViewMonth(today.getMonth()); }} style={{ fontSize: 12, padding: '2px 8px', marginLeft: 8 }}>Today</button>
      </div>

      {/* ── Calendar grid ── */}
      <div style={{ border: '1px solid var(--color-border)', borderRadius: 8, overflow: 'hidden' }}>

        {/* Day-of-week header */}
        <div style={{ display: 'grid', gridTemplateColumns: GRID }}>
          {DAY_HDR.map((d, i) => {
            const wknd = i === 0 || i === 6;
            return (
              <div key={d} style={{
                padding: '5px 0', textAlign: 'center', fontSize: 11, fontWeight: 600, overflow: 'hidden',
                borderBottom: '1px solid var(--color-border)',
                background: isBiz && wknd ? 'rgba(0,0,0,0.03)' : 'var(--color-card, #f9fafb)',
                color: isBiz && wknd ? 'var(--color-border)' : 'var(--color-text-muted)',
              }}>{d}</div>
            );
          })}
        </div>

        {/* Weeks */}
        {weeks.map((week, wi) => (
          <div key={wi} style={{ display: 'grid', gridTemplateColumns: GRID }}>
            {week.map((day, di) => {
              const inMonth = day.getMonth() === viewMonth;
              const isToday = isSameDay(day, today);
              const wknd = isWeekend(day);
              const nonWorking = isBiz && wknd;
              // In business-days mode, hide projected chips on weekends; still show anchored/completed
              const all = tasksOnDay(day);
              const chips = nonWorking ? all.filter(p => !p.isProjected) : all;
              const overflow = chips.length - 3;

              let bg: string | undefined;
              if (nonWorking) bg = 'rgba(0,0,0,0.03)';
              else if (isToday) bg = 'rgba(59,130,246,0.05)';
              else if (!inMonth) bg = 'rgba(0,0,0,0.02)';

              return (
                <div key={di} style={{
                  minHeight: 80, overflow: 'hidden', padding: '4px 5px',
                  borderRight: di < 6 ? '1px solid var(--color-border)' : undefined,
                  borderBottom: wi < weeks.length - 1 ? '1px solid var(--color-border)' : undefined,
                  background: bg,
                }}>
                  {/* Date number */}
                  <div style={{ marginBottom: 3 }}>
                    {isToday && !nonWorking ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, borderRadius: '50%', background: 'var(--color-primary,#3b82f6)', color: '#fff', fontSize: 11, fontWeight: 700 }}>
                        {day.getDate()}
                      </span>
                    ) : (
                      <span style={{ fontSize: 12, fontWeight: inMonth && !nonWorking ? 400 : 300, color: nonWorking || !inMonth ? 'var(--color-border)' : undefined }}>
                        {day.getDate()}
                      </span>
                    )}
                  </div>

                  {/* Chips */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                    {chips.slice(0, 3).map(pt => {
                      const isStart = isSameDay(day, pt.start);
                      return (
                        <div
                          key={pt.task.taskId}
                          title={`${pt.task.title}${pt.task.points > 0 ? ` · ${pt.task.points}pt` : ''}  ${pt.isProjected ? '(projected)' : '(scheduled)'}`}
                          onClick={onTaskClick ? () => onTaskClick(pt.task) : undefined}
                          style={{
                            fontSize: 10, padding: '1px 4px', borderRadius: 3,
                            background: pt.color, color: '#fff',
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0,
                            opacity: !inMonth ? 0.4 : pt.isProjected ? 0.78 : 1,
                            borderLeft: pt.isProjected ? '2px dashed rgba(255,255,255,0.5)' : undefined,
                            cursor: onTaskClick ? 'pointer' : 'default',
                          }}
                        >
                          {isStart ? `${pt.task.title}${pt.task.points > 0 ? ` ·${pt.task.points}` : ''}` : '╌'}
                        </div>
                      );
                    })}
                    {overflow > 0 && (
                      <div style={{ fontSize: 10, color: 'var(--color-text-muted)', paddingLeft: 2, whiteSpace: 'nowrap' }}>+{overflow} more</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {tasks.length === 0 && (
        <p className="muted" style={{ marginTop: 14, textAlign: 'center', fontSize: 13 }}>
          Add tasks with points to see the projected schedule.
        </p>
      )}
    </div>
  );
}
